import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import format from 'string-template';
import * as Sentry from '@sentry/browser';

import FileSelectButton from '../../../../../../../components/FileSelectButton';
import { PrimaryButton } from '../../../../../../../components/Button/ActionButtons';
import ConfirmationModal from '../../../../../../../components/v2/ConfirmationModal';
import getSessionVideoFileError from '../../../../../../../components/SessionVideoUpload/getSessionVideoFileError';
import SessionVideoUploadStatus from '../../../../../../../components/SessionVideoUpload/SessionVideoUploadStatus';
import sessionVideoTexts from '../../../../../../../components/SessionVideoUpload/texts.json';
import FirebaseContext from '../../../../../../../context/FirebaseContext';
import LongSessionUploadContext, {
  LongSessionUploadActionsContext,
  UploadKind,
  UploadState,
} from '../../../../../../../context/LongSessionUploadContext';
import useSessionStore from '../../../../../../../hooks/useSessionStore';
import useStorage from '../../../../../../../hooks/useStorage';
import Product from '../../../../../../../Model/Product';
import {
  LongSessionFailureCause,
  WorkoutPublishStatus,
  getWelcomeVideoSourcePath,
  isUploadInterrupted,
} from '../../../../../../../utils/longSession';
import { formatDuration, TimeDurationFormat } from '../../../../../../../utils/time';
import { transformVideoUrl } from '../../../../../../../utils/video';
import useToast from '../../../../../../hooks/useToast';
import { postJson } from '../../../../../../utils/remoteError';
import {
  Actions,
  ErrorText,
  HelperText,
  ReadyDetails,
  SelectButton,
  StatusCard,
  StatusRow,
} from '../../../../../../../components/SessionVideoUpload/styles';
import {
  ErrorTag,
  LightTag,
  NoticeTag,
  SuccessTag,
} from '../../../../../../../components/Tags';

import {
  Heading,
  LiveDetails,
  LivePreview,
  LiveVideo,
  Panel,
  Section,
  Summary,
} from './styles';
import texts from './texts.json';

/**
 * A product's welcome video on its card in Config, Products: upload one, watch it, replace it or
 * remove it.
 *
 * It opens collapsed, so a long card stays short: the header says where the video stands, and the
 * player only loads once the coach opens it. Collapsing never stops an upload, which the upload
 * provider holds.
 *
 * The upload runs through the long-session pipeline, so this reads the same stages a long session's
 * panel does, from the product's `welcomeVideoUpload`. The live video is `welcomeVideo`, which only the
 * server writes: a replacement goes live when it is ready, and removing goes through the server,
 * which also holds the Vimeo token.
 *
 * The products page fetches its products once, so this opens the product's own document: firestorter
 * listens to it while this observer renders, and the stages show up as the server writes them. The
 * owner comes from the card's loaded product instead, so a file picked before the first snapshot
 * still lands in the owner's folder.
 */
const WelcomeVideoSection = ({ productId, coachId }) => {
  const product = useMemo(() => new Product(productId), [productId]);
  const { firebase: { remote } } = useContext(FirebaseContext);
  const { uploads } = useContext(LongSessionUploadContext);
  const { startUpload, cancelUpload } = useContext(LongSessionUploadActionsContext);
  const { userId } = useSessionStore();
  const { deleteAttachment } = useStorage();
  const { showToast } = useToast();
  const [error, setError] = useState('');
  const [isPreparing, setIsPreparing] = useState(false);
  const [isConfirmingRemove, setIsConfirmingRemove] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const uploadKey = `product-${productId}`;
  const upload = uploads[uploadKey];
  const liveVideo = product.welcomeVideo;
  const pendingUpload = product.welcomeVideoUpload;
  const publishStatus = pendingUpload?.publishStatus;
  const stage = pendingUpload?.videoProcessing?.stage;

  const hasLiveUpload = upload?.state === UploadState.UPLOADING;
  // This tab's failed transfer counts too: if recording the stop failed, the product still reads UPLOADING.
  const hasFailed = publishStatus === WorkoutPublishStatus.FAILED || upload?.state === UploadState.FAILED;
  /*
    At UPLOADING with no entry in this browser session: the bytes died with a closed tab. Not while
    preparing, when the product already reads UPLOADING but the transfer has not registered yet.
  */
  const canResume = isUploadInterrupted({ publishStatus, stage, upload }) && !isPreparing;
  /*
    The provider keeps a finished transfer's entry, but the product says everything once the server
    has the file. An entry still counts while it is in flight or failed; a finished one only adds
    something while the product still reads UPLOADING, and nothing once the video went live.
  */
  const showsUploadStatus = !!pendingUpload || hasLiveUpload || upload?.state === UploadState.FAILED;
  /*
    As a long session: a new video only while none is on its way. The server ignores a finalized
    upload unless it reads PENDING at UPLOADING, so a replace offered mid-processing would be lost.
  */
  const acceptsNewVideo = !pendingUpload || canResume || hasFailed;
  /*
    The live video can always be removed, except while a replacement is on its way: that one goes
    live by itself once ready. A failed, stopped or cut-off replacement is going nowhere.
  */
  const isReplacing = hasLiveUpload || (!!pendingUpload && !hasFailed && !canResume);
  // A coach who stopped the upload themselves does not need it reported as a fault.
  const wasCancelled = hasFailed && pendingUpload?.videoProcessing?.error?.cause === LongSessionFailureCause.CANCELLED;
  const aspectRatio = liveVideo?.width && liveVideo?.height ? `${liveVideo.width} / ${liveVideo.height}` : '16 / 9';
  const duration = liveVideo?.durationSeconds
    ? formatDuration(liveVideo.durationSeconds, TimeDurationFormat.MINUTES_SECONDS, { trim: false })
    : '';

  const handleFileSelected = useCallback(async (event) => {
    const [file] = event.target.files || [];
    if (!file) {
      return;
    }

    const validationError = getSessionVideoFileError(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');

    const storagePath = getWelcomeVideoSourcePath(coachId, productId, file.name);
    const previousPath = pendingUpload?.sourceStoragePath;

    setIsPreparing(true);
    try {
      /*
        A failed or abandoned attempt's file would otherwise sit in the folder forever. The live
        video's file stays: the server deletes it once a replacement goes live.
      */
      if (previousPath && previousPath !== storagePath && previousPath !== liveVideo?.sourceStoragePath) {
        await deleteAttachment(previousPath, { ignoreNotFound: true });
      }
      await product.startWelcomeVideoUpload(storagePath, userId);
    } catch (prepareError) {
      Sentry.captureException(prepareError, {
        extra: { message: 'Failed to prepare a welcome video upload', productId },
      });
      setError(texts.prepareFailed);
      setIsPreparing(false);
      return;
    }

    // Registers the transfer's entry before the call returns, so preparing can end without a gap.
    const transfer = startUpload({
      key: uploadKey,
      kind: UploadKind.WELCOME_VIDEO,
      coachId,
      storagePath,
      file,
      onStopped: (stopped) => product.markWelcomeVideoUploadStopped(stopped),
    });
    setIsPreparing(false);
    await transfer;
  }, [
    product,
    productId,
    coachId,
    pendingUpload,
    liveVideo,
    uploadKey,
    userId,
    startUpload,
    deleteAttachment,
  ]);

  const handleRemove = useCallback(async () => {
    setIsConfirmingRemove(false);
    setIsRemoving(true);
    try {
      await postJson(remote, 'welcomeVideo-remove', { productId }, texts.removeFailed);
      showToast(texts.removeSuccess);
    } catch (removeError) {
      Sentry.captureException(removeError, {
        extra: { message: 'Failed to remove a welcome video', productId },
      });
      showToast(texts.removeFailed, { error: true });
    } finally {
      setIsRemoving(false);
    }
  }, [remote, productId, showToast]);

  // What the status card says, as tags and one line, for the header the coach sees while it is closed.
  const renderSummary = () => {
    let videoTag = null;
    if (liveVideo) {
      videoTag = (
        <SuccessTag>{duration ? format(texts.summary.live, { duration }) : texts.summary.liveNoLength}</SuccessTag>
      );
    } else if (!showsUploadStatus) {
      videoTag = <LightTag>{texts.summary.notSet}</LightTag>;
    }

    let uploadTag = null;
    let line = texts.description;
    if (wasCancelled) {
      uploadTag = <LightTag>{texts.summary.stopped}</LightTag>;
      line = texts.summary.needsNewUpload;
    } else if (hasFailed) {
      uploadTag = <ErrorTag>{texts.summary.failed}</ErrorTag>;
      line = texts.summary.needsNewUpload;
    } else if (canResume) {
      uploadTag = <NoticeTag>{texts.summary.interrupted}</NoticeTag>;
      line = texts.summary.needsNewUpload;
    } else if (hasLiveUpload) {
      uploadTag = <NoticeTag>{format(texts.summary.uploading, { progress: upload.progress })}</NoticeTag>;
      line = texts.summary.keepTabOpen;
    } else if (showsUploadStatus) {
      uploadTag = <NoticeTag>{texts.summary.processing}</NoticeTag>;
      line = texts.summary.processingDetails;
    }

    return (
      <>
        <Heading>{texts.title}</Heading>
        {videoTag}
        {uploadTag}
        <Summary>{line}</Summary>
      </>
    );
  };

  return (
    <Panel title={renderSummary()} TransitionProps={{ unmountOnExit: true }} disableGutters>
      <Section>
        {!!liveVideo && (
          <StatusCard>
            <LivePreview>
              <LiveVideo
                src={transformVideoUrl(liveVideo.videoUrl)}
                title={texts.title}
                $aspectRatio={aspectRatio}
                allow="fullscreen; picture-in-picture"
                allowFullScreen
              />
              <LiveDetails>
                <StatusRow>{texts.ready}</StatusRow>
                {!!duration && <ReadyDetails>{format(texts.duration, { duration })}</ReadyDetails>}
                {!isReplacing && (
                  <Actions>
                    <PrimaryButton
                      variant="info"
                      size="small"
                      disabled={isRemoving}
                      onClick={() => setIsConfirmingRemove(true)}
                    >
                      {texts.remove}
                    </PrimaryButton>
                  </Actions>
                )}
              </LiveDetails>
            </LivePreview>
          </StatusCard>
        )}
        {showsUploadStatus && (
          <SessionVideoUploadStatus
            publishStatus={publishStatus}
            videoProcessing={pendingUpload?.videoProcessing}
            upload={upload}
            canResume={canResume}
            copy={texts.status}
            notice={liveVideo ? texts.replacing : ''}
            onCancel={() => cancelUpload(uploadKey)}
          />
        )}
        {!liveVideo && !pendingUpload && <HelperText>{texts.tip}</HelperText>}
        {!hasLiveUpload && acceptsNewVideo && (
          <FileSelectButton
            ButtonComponent={SelectButton}
            onSelect={handleFileSelected}
            disabled={isPreparing || isRemoving}
            multiple={false}
            extraInputProps={{ accept: 'video/*' }}
          >
            {canResume && sessionVideoTexts.sendAgain}
            {!canResume && (liveVideo ? texts.replaceFile : texts.selectFile)}
          </FileSelectButton>
        )}
        {!!error && <ErrorText>{error}</ErrorText>}
        {isConfirmingRemove && (
          <ConfirmationModal
            open
            title={texts.removeConfirmTitle}
            description={texts.removeConfirmDescription}
            onClose={() => setIsConfirmingRemove(false)}
            onConfirm={handleRemove}
          />
        )}
      </Section>
    </Panel>
  );
};

WelcomeVideoSection.propTypes = {
  productId: PropTypes.string.isRequired,
  /** The product's owner, whose folder the upload goes to. Not necessarily the uploader. */
  coachId: PropTypes.string.isRequired,
};

export default observer(WelcomeVideoSection);
